import { useEffect, useState, useCallback } from "react";
import { Plus, DollarSign, ShoppingBag, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { apiErr } from "@/lib/api";
import { PageHeader, StatCard, Loading } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const today = () => new Date().toISOString().slice(0, 10);
const empty = () => ({ date: today(), amount: "", orders: "", channel: "dine-in", notes: "" });

export default function Sales() {
  const [summary, setSummary] = useState(null);
  const [sales, setSales] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());

  const load = useCallback(async () => {
    const [s, l] = await Promise.all([api.get("/sales/summary"), api.get("/sales")]);
    setSummary(s.data); setSales(l.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/sales", { ...form, amount: parseFloat(form.amount) || 0, orders: parseInt(form.orders) || 1 });
      toast.success("Sale recorded");
      setForm(empty()); setOpen(false); load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  if (!summary) return <Loading />;
  const fmt = (n) => `$${Number(n || 0).toLocaleString()}`;

  return (
    <div data-testid="sales-page">
      <PageHeader title="Sales Reports" subtitle="Daily revenue and order tracking."
        action={<Button data-testid="add-sale-btn" onClick={() => setOpen(true)} className="bg-primary hover:bg-[#B35A42] text-primary-foreground"><Plus className="h-4 w-4 mr-1" /> Record sale</Button>} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={DollarSign} label="Total Revenue" value={fmt(summary.total_revenue)} />
        <StatCard icon={ShoppingBag} label="Total Orders" value={summary.total_orders} accent="text-secondary" />
        <StatCard icon={TrendingUp} label="Avg Order Value" value={fmt(summary.avg_order_value)} />
      </div>

      <div className="bg-card border border-border rounded-xl p-6 mt-6">
        <h3 className="text-lg font-medium mb-4">Daily revenue</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={summary.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EBE6DE" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#736B5E" }} tickFormatter={(d) => d?.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: "#736B5E" }} tickFormatter={(v) => `$${v}`} width={48} />
              <Tooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: 8, border: "1px solid #EBE6DE" }} />
              <Bar dataKey="amount" fill="#C86A51" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden mt-6">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Date</TableHead><TableHead>Channel</TableHead>
              <TableHead className="text-right">Orders</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead>Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sales.slice(0, 30).map((s) => (
              <TableRow key={s.id} data-testid={`sale-row-${s.id}`}>
                <TableCell className="font-medium">{s.date}</TableCell>
                <TableCell className="capitalize text-muted-foreground">{s.channel}</TableCell>
                <TableCell className="text-right">{s.orders}</TableCell>
                <TableCell className="text-right font-medium">{fmt(s.amount)}</TableCell>
                <TableCell className="text-muted-foreground">{s.notes}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Record a sale</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Date</Label>
                <Input data-testid="sale-form-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Channel</Label>
                <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                  <SelectTrigger data-testid="sale-form-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dine-in">Dine-in</SelectItem>
                    <SelectItem value="takeout">Takeout</SelectItem>
                    <SelectItem value="delivery">Delivery</SelectItem>
                    <SelectItem value="online">Online</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Amount ($)</Label>
                <Input data-testid="sale-form-amount" type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Orders</Label>
                <Input data-testid="sale-form-orders" type="number" required value={form.orders} onChange={(e) => setForm({ ...form, orders: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="submit" data-testid="sale-form-submit" className="bg-primary hover:bg-[#B35A42] text-primary-foreground">Record sale</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
